/**
 * Reine Abbau-Rechnung der ActionBar (Spec:
 * docs/superpowers/specs/2026-07-27-actionbar-mobile-design.md §3.2).
 * Kein DOM-Zugriff — Eingabe sind gemessene Breiten, Ausgabe ist der Layout-Zustand.
 *
 * Rangfolge: (1) Button-Beschriftungen gemeinsam zu Icons, (2) Statistikwerte
 * einzeln von rechts nach links, (3) der Titel kürzt per Ellipsis — Letzteres
 * macht CSS, die Funktion gibt dafür schlicht alle Werte als versteckt zurück.
 */

export const DEFAULT_HYSTERESIS = 24;
/**
 * Puffer gegen Rundung: `offsetWidth` liefert ganze Pixel, jedes gemessene
 * Element kann also bis zu einem halben Pixel verlieren. Bei den ~6 Elementen
 * einer Leiste sind das höchstens ~3px. Größer sollte der Puffer nicht sein —
 * die Leiste vergibt sonst Platz, den sie hat: die Wantlist verlöre bei 390px
 * ihren zweiten Leitwert, obwohl er um 4px hineinpasst.
 */
export const DEFAULT_SAFETY = 4;

export function computeActionBarLayout({
  containerWidth,
  innerGap,
  outerGap,
  sepWidth,
  leftFixedFullWidth = 0,
  leftFixedIconWidth = 0,
  leftFixedCount = 0,
  stats = [],
  rightFullWidth = 0,
  rightIconWidth = 0,
  previousHiddenStatIds = [],
  hysteresis = DEFAULT_HYSTERESIS,
  safety = DEFAULT_SAFETY,
}) {
  const solve = (width) => {
    let visible = stats.length;
    let actionsCompact = false;

    const fits = () => {
      const n = visible;
      const seps = Math.max(0, n - 1);
      const statsWidth = stats.slice(0, n).reduce((sum, s) => sum + s.width, 0);
      const leftFixed = actionsCompact ? leftFixedIconWidth : leftFixedFullWidth;
      const leftFlexItems = leftFixedCount + n + seps;
      const leftWidth =
        leftFixed +
        statsWidth +
        sepWidth * seps +
        innerGap * Math.max(0, leftFlexItems - 1);
      const rightWidth = actionsCompact ? rightIconWidth : rightFullWidth;
      const between = leftFlexItems > 0 && rightWidth > 0 ? outerGap : 0;
      return leftWidth + between + rightWidth <= width - safety;
    };

    if (fits()) return { actionsCompact: false, hiddenStatIds: [] };

    // Stufe 1: alle Button-Beschriftungen gemeinsam zu Icons
    actionsCompact = true;

    // Stufe 2: Werte von rechts nach links ausblenden
    const hiddenStatIds = [];
    while (!fits() && visible > 0) {
      visible -= 1;
      hiddenStatIds.push(stats[visible].id);
    }

    // Stufe 3: passt es dann noch nicht, kürzt der Titel per CSS — nichts zu tun.
    return { actionsCompact, hiddenStatIds };
  };

  const now = solve(containerWidth);

  // Hysterese: ein Wert kehrt erst zurück, wenn ihn auch eine um `hysteresis`
  // schmalere Leiste nicht wieder ausblenden würde (kein Zittern an der Kippkante).
  const existing = new Set(stats.map((s) => s.id));
  const prev = previousHiddenStatIds.filter((id) => existing.has(id));
  const wouldReturn = prev.some((id) => !now.hiddenStatIds.includes(id));
  if (wouldReturn) {
    const shrunk = solve(containerWidth - hysteresis);
    const blocked = prev.some(
      (id) => !now.hiddenStatIds.includes(id) && shrunk.hiddenStatIds.includes(id)
    );
    if (blocked) return shrunk;
  }
  return now;
}
